import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtTime, fmtDateTime, errMsg } from "@/lib/api";
import { Empty, ConfBadge, StatusPill, SectionHead } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search as SearchIcon, Loader2, FileAudio, Mic2, ListChecks } from "lucide-react";

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [speaker, setSpeaker] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [speakers, setSpeakers] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/speakers").then(({ data }) => setSpeakers(data)).catch(() => {}); }, []);

  const run = async (e) => {
    e?.preventDefault();
    if (!q.trim() && speaker === "all" && !from && !to) { toast.info("Masukkan kata kunci atau filter"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/search", {
        params: { q, speaker: speaker === "all" ? "" : speaker, date_from: from, date_to: to },
      });
      setRes(data);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const total = res ? res.transcripts.length + res.meetings.length + res.action_items.length : 0;

  return (
    <div className="space-y-7" data-testid="search-page">
      <div>
        <p className="label-xs">Global Search</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Search</h1>
        <p className="mt-2 text-sm text-slate-500">Cari lintas transkrip, rapat, speaker, topik, dan action items.</p>
      </div>

      <form onSubmit={run} className="clay p-6 space-y-4" data-testid="search-form">
        <div className="relative">
          <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Contoh: anggaran, vendor, keputusan…"
            className="h-13 pl-12 pr-4 rounded-xl text-base" style={{ height: 52 }} data-testid="global-search-input" />
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <Select value={speaker} onValueChange={setSpeaker}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="search-speaker-filter"><SelectValue placeholder="Semua speaker" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Speaker</SelectItem>
              {speakers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-11 rounded-xl" data-testid="search-date-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-11 rounded-xl" data-testid="search-date-to" />
          <Button type="submit" className="h-11 rounded-xl grad-brand text-white" disabled={busy} data-testid="search-submit-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Cari"}
          </Button>
        </div>
      </form>

      {res && (
        <div data-testid="search-results">
          <SectionHead title={`${total} hasil ditemukan`} sub={q ? `untuk “${q}”` : "berdasarkan filter"} />
          <Tabs defaultValue="transcripts">
            <TabsList className="rounded-xl bg-slate-100/80 p-1">
              <TabsTrigger value="transcripts" className="rounded-lg" data-testid="search-tab-transcripts">Transkrip ({res.transcripts.length})</TabsTrigger>
              <TabsTrigger value="meetings" className="rounded-lg" data-testid="search-tab-meetings">Rapat ({res.meetings.length})</TabsTrigger>
              <TabsTrigger value="actions" className="rounded-lg" data-testid="search-tab-actions">Action Items ({res.action_items.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="transcripts" className="mt-6">
              <div className="clay p-6">
                {res.transcripts.length === 0 ? <Empty icon={Mic2} title="Tidak ada transkrip cocok" sub="Coba kata kunci lain." /> : (
                  <div className="space-y-1">
                    {res.transcripts.map((t) => (
                      <Link key={t.id} to={`/meetings/${t.meeting_id}`} className="block rounded-xl border-l-2 border-slate-100 px-4 py-3.5 hover:border-brand-600 hover:bg-brand-50/40 transition-colors" data-testid={`search-transcript-${t.id}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs text-slate-400">{fmtTime(t.start)}</span>
                          <span className="text-sm font-semibold text-slate-800">{t.speaker_name}</span>
                          <ConfBadge value={t.confidence} />
                          <span className="text-xs text-slate-400">· {t.meeting_title}</span>
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{t.text}</p>
                        <p className="mt-1.5 text-[11px] text-brand-600">Klik untuk membuka & memutar audio pada timestamp ini</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="meetings" className="mt-6">
              <div className="clay p-6">
                {res.meetings.length === 0 ? <Empty icon={FileAudio} title="Tidak ada rapat cocok" sub="Coba filter tanggal atau kata kunci lain." /> : (
                  <div className="divide-y divide-slate-100">
                    {res.meetings.map((m) => (
                      <Link key={m.id} to={`/meetings/${m.id}`} className="flex items-center gap-4 py-4 first:pt-0 group" data-testid={`search-meeting-${m.id}`}>
                        <div className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-brand-50 text-brand-600 group-hover:grad-brand group-hover:text-white transition-colors"><FileAudio size={17} /></div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-800">{m.title}</p>
                          <p className="text-xs text-slate-500">{fmtDateTime(m.scheduled_at)} · {m.division || "—"}</p>
                        </div>
                        <StatusPill status={m.status} />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="actions" className="mt-6">
              <div className="clay p-6">
                {res.action_items.length === 0 ? <Empty icon={ListChecks} title="Tidak ada action item cocok" sub="Coba kata kunci lain." /> : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {res.action_items.map((it) => (
                      <Link key={it.id} to={`/meetings/${it.meeting_id}`} className="rounded-2xl border border-slate-200/70 p-5 hover:shadow-clay transition-shadow" data-testid={`search-action-${it.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-800">{it.task}</p>
                          <StatusPill status={it.priority} />
                        </div>
                        <p className="mt-3 text-xs text-slate-500">PIC {it.owner} · Deadline {it.deadline}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
